<template>
  <v-container fluid class="dashboard-pyg-container">
    <!-- Main P&G Content -->
    <div v-if="!isLoading">
      <!-- Title Section -->
      <div class="pyg-header mb-4">
        <h2 class="pyg-title">Cuenta de resultados</h2>
        <div v-if="fiscalYearsText" class="pyg-subtitle">
          {{ hasMultipleFiscalYears ? 'Ejercicios' : 'Ejercicio' }}: {{ fiscalYearsText }}
        </div>
      </div>

      <!-- P&G Table -->
      <pyg-table
        v-if="hasPygData"
        :table-data="processedTableData"
        :selected-ejercicio="selectedFiscalYear"
        :selected-ejercicios="selectedFiscalYears"
        :is-multi-year="isMultiYear"
      />

      <!-- P&G Chart -->
      <pyg-chart
        v-if="hasPygData"
        :chart-data="processedChartData"
        :selected-ejercicio="selectedFiscalYear"
        :selected-ejercicios="selectedFiscalYears"
        :is-multi-year="isMultiYear"
      />

      <!-- Empty State -->
      <v-card v-if="!hasPygData && !isLoading" class="empty-state-card">
        <v-card-text class="d-flex align-center justify-center fill-height">
          <div class="text-center pa-8">
            <v-icon x-large color="grey lighten-1">mdi-chart-line</v-icon>
            <h2 class="mt-4 grey--text text--darken-1">No hay datos de P&G disponibles</h2>
            <p class="mt-2 text--secondary">
              No se encontraron datos de Pérdidas y Ganancias para el ejercicio seleccionado
            </p>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { LoadPyGData } from '@/modules/dashboard/application/LoadPyGData';
import PygTable from '@/components/pyg/PygTable.vue';
import PygChart from '@/components/pyg/PygChart.vue';

export default {
  name: 'DashboardPyG',

  components: {
    PygTable,
    PygChart
  },

  props: {
    selectedFiscalYear: {
      type: Number,
      default: null
    },
    selectedFiscalYears: {
      type: Array,
      default: () => []
    },
    pygGrupos: {
      type: Array,
      default: () => []
    },
    pygData: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      processedTableData: [],
      processedChartData: [],
      totalSales: 0,
      result: 0,
      isMultiYear: false
    };
  },

  computed: {
    hasPygData() {
      return this.processedTableData && this.processedTableData.length > 0;
    },

    hasMultipleFiscalYears() {
      return this.selectedFiscalYears && this.selectedFiscalYears.length > 1;
    },

    fiscalYearsText() {
      if (this.hasMultipleFiscalYears) {
        return this.selectedFiscalYears.join(', ');
      } else if (this.selectedFiscalYear) {
        return String(this.selectedFiscalYear);
      }
      return '';
    }
  },

  methods: {
    processDataForYear() {
      const loader = new LoadPyGData(null);

      // Detect if multiple fiscal years are selected
      if (this.hasMultipleFiscalYears) {
        // MULTI-YEAR MODE
        if (!this.pygGrupos.length || !this.pygData.length) {
          this.processedTableData = [];
          this.processedChartData = {};
          this.totalSales = 0;
          this.result = 0;
          this.isMultiYear = true;
          return;
        }

        const multiYearData = loader.processDataForMultipleYears(
          this.pygGrupos,
          this.pygData,
          this.selectedFiscalYears
        );

        this.processedTableData = multiYearData.tableData;
        this.processedChartData = multiYearData.chartData;
        // For multi-year, we don't have a single totalSales/result
        this.totalSales = 0;
        this.result = 0;
        this.isMultiYear = true;
      } else {
        // SINGLE-YEAR MODE (current behavior)
        const year = this.selectedFiscalYears && this.selectedFiscalYears.length > 0
          ? this.selectedFiscalYears[0]
          : this.selectedFiscalYear;

        if (!year || !this.pygGrupos.length || !this.pygData.length) {
          this.processedTableData = [];
          this.processedChartData = [];
          this.totalSales = 0;
          this.result = 0;
          this.isMultiYear = false;
          return;
        }

        const processed = loader.processDataForYear(
          this.pygGrupos,
          this.pygData,
          year
        );

        this.processedTableData = processed.tableData;
        this.processedChartData = processed.chartData;
        this.totalSales = processed.totalSales;
        this.result = processed.result;
        this.isMultiYear = false;
      }
    }
  },

  watch: {
    selectedFiscalYear: {
      handler: function() {
        this.processDataForYear();
      },
      immediate: true
    },

    selectedFiscalYears: {
      handler: function() {
        this.processDataForYear();
      },
      deep: true,
      immediate: true
    },

    pygGrupos: {
      handler: function() {
        this.processDataForYear();
      },
      deep: true
    },

    pygData: {
      handler: function() {
        this.processDataForYear();
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.dashboard-pyg-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  padding: 20px;
}

.pyg-header {
  margin-bottom: 16px;
}

.pyg-title {
  color: var(--v-primary-base);
  font-size: 28px;
  font-weight: 500;
  margin-bottom: 4px;
}

.pyg-subtitle {
  color: #666;
  font-size: 16px;
  font-weight: 400;
}

.warning-text {
  font-size: 13px !important;
  width: 100%;
  margin: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.empty-state-card {
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.empty-state-card .v-card__text {
  flex: 1;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .dashboard-pyg-container {
    padding: 16px;
  }

  .pyg-title {
    font-size: 24px;
  }

  .pyg-subtitle {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .dashboard-pyg-container {
    padding: 12px;
  }

  .pyg-title {
    font-size: 20px;
  }

  .pyg-subtitle {
    font-size: 13px;
  }
}
</style>
